import { useRef } from 'react';
import { apiDelete, apiPost } from '../../lib/api-client';
import { Button } from '../ui/Button';

export interface ProposalFileLink {
  name: string;
  url: string;
  path: string;
  contentType?: string;
}

export function openableLink(url?: string): string {
  return url && /^https?:\/\//i.test(url) ? url : '';
}

function fileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result || '');
      const at = value.indexOf(',');
      resolve(at >= 0 ? value.slice(at + 1) : value);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read that file'));
    reader.readAsDataURL(file);
  });
}

export async function uploadProposalFile(proposalId: string, file: File): Promise<Record<string, unknown>> {
  const dataBase64 = await fileBase64(file);
  return apiPost(`/api/proposals/${proposalId}/files`, { name: file.name, mimeType: file.type || 'application/octet-stream', dataBase64 });
}

export async function deleteProposalFile(proposalId: string, path: string): Promise<Record<string, unknown>> {
  return apiDelete(`/api/proposals/${proposalId}/files?path=${encodeURIComponent(path)}`);
}

export async function uploadQuotationFile(quotationId: string, file: File): Promise<Record<string, unknown>> {
  const dataBase64 = await fileBase64(file);
  return apiPost(`/api/quotations/${quotationId}/files`, { name: file.name, mimeType: file.type || 'application/octet-stream', dataBase64 });
}

export async function deleteQuotationFile(quotationId: string, path: string): Promise<Record<string, unknown>> {
  return apiDelete(`/api/quotations/${quotationId}/files?path=${encodeURIComponent(path)}`);
}

export function filesOf(row: { externalFiles?: unknown } | null | undefined): ProposalFileLink[] {
  const raw = row?.externalFiles;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const file = item as Partial<ProposalFileLink>;
    if (!file.name || !file.url || !file.path) return [];
    return [{ name: file.name, url: file.url, path: file.path, contentType: file.contentType }];
  });
}

export function ProposalFileLinks({
  files,
  proposalUrl,
  proposalLabel,
  quotationUrl,
  quotationLabel,
  locked,
  busy,
  onPick,
  onRemove,
}: {
  files: ProposalFileLink[];
  proposalUrl?: string;
  proposalLabel?: string;
  quotationUrl?: string;
  quotationLabel?: string;
  locked?: boolean;
  busy?: boolean;
  onPick?: (file: File) => void;
  onRemove?: (path: string) => void;
}) {
  const proposalHref = openableLink(proposalUrl);
  const quotationHref = openableLink(quotationUrl);
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="space-y-2">
      {proposalHref ? <a className="block text-sm text-[var(--bo-link)]" href={proposalHref} target="_blank" rel="noreferrer">{proposalLabel || 'Proposal'}</a> : null}
      {files.map((file) => (
        <div key={file.path} className="flex items-center justify-between gap-2 text-sm">
          {openableLink(file.url) ? <a className="min-w-0 truncate text-[var(--bo-link)]" href={file.url} target="_blank" rel="noreferrer">{file.name}</a> : <span className="min-w-0 truncate">{file.name}</span>}
          {!locked && onRemove ? <button type="button" className="shrink-0 text-red-600" disabled={busy} onClick={() => onRemove(file.path)}>Remove</button> : null}
        </div>
      ))}
      {quotationHref ? <a className="block text-sm text-[var(--bo-link)]" href={quotationHref} target="_blank" rel="noreferrer">{quotationLabel || 'Quotation'}</a> : null}
      {!locked && onPick ? (
        <>
          <Button variant="outline" disabled={busy} onClick={() => inputRef.current?.click()}>{busy ? 'Uploading…' : 'Attach a file'}</Button>
          <input ref={inputRef} className="hidden" type="file" disabled={busy} onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) onPick(file); }} />
        </>
      ) : null}
    </div>
  );
}
